/**
 * Array utility functions
 */
/**
 * Remove duplicates from an array
 * @param items - The array to remove duplicates from
 * @param getValue - Optional function to get unique value from each item
 * @returns Array with duplicates removed
 */
function distinct(items, getValue) {
    const distinctItems = [];
    const uniqueValues = new Set();
    const _getValue = getValue || ((i) => `${i}`);
    for (const item of items) {
        const value = _getValue(item);
        if (!uniqueValues.has(value)) {
            uniqueValues.add(value);
            distinctItems.push(item);
        }
    }
    return distinctItems;
}
/**
 * Split array into chunks of specified size
 * @param arr - The array to chunk
 * @param chunkSize - Size of each chunk
 * @returns Array of chunks
 */
function chunk(arr, chunkSize) {
    const chunkedArray = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
        chunkedArray.push(arr.slice(i, i + chunkSize));
    }
    return chunkedArray;
}
/**
 * Group array items by a key function
 * @param array - The array to group
 * @param keyFn - Function to extract grouping key from each item
 * @returns Object with keys as group names and values as arrays of items
 */
function groupBy(array, keyFn) {
    const groups = {};
    for (const item of array) {
        const key = keyFn(item);
        if (!groups[key]) {
            groups[key] = [];
        }
        groups[key].push(item);
    }
    return groups;
}
/**
 * Get random elements from array
 * @param array - The array to select from
 * @param count - Number of elements to select
 * @returns Array of randomly selected elements
 */
function arrayRandomElements(array, count) {
    if (count >= array.length)
        return [...array];
    if (count <= 0)
        return [];
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, count);
}
/**
 * Get single random element from array
 * @param array - The array to select from
 * @returns Random element or undefined if array is empty
 */
function arrayRandomElement(array) {
    if (array.length === 0)
        return undefined;
    return array[Math.floor(Math.random() * array.length)];
}
/**
 * Get random element with seed for consistent results
 * @param array - The array to select from
 * @param seed - Seed string for consistent randomization
 * @returns Random element or undefined if array is empty
 */
function getRandomElementWithSeed(array, seed) {
    if (array.length === 0)
        return undefined;
    // Simple hash function for seed
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        const char = seed.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    const index = Math.abs(hash) % array.length;
    return array[index];
}
/**
 * Get last element of array
 * @param array - The array to get last element from
 * @returns Last element or undefined if array is empty
 */
function arrayLast(array) {
    return array[array.length - 1];
}
/**
 * Shuffle array in place
 * @param array - The array to shuffle
 * @returns New shuffled array
 */
function shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
}
/**
 * Subtract second array from first array
 * @param arr1 - First array
 * @param arr2 - Array of items to remove from first array
 * @returns Array with items from arr2 removed
 */
function subtractArrays(arr1, arr2) {
    const set2 = new Set(arr2);
    return arr1.filter((item) => !set2.has(item));
}
/**
 * Pad array at start to minimum size
 * @param arr - The array to pad
 * @param minSize - Minimum size of resulting array
 * @param padWith - Value to use for padding
 * @returns Padded array
 */
function padArrayStart(arr, minSize, padWith) {
    if (arr.length >= minSize)
        return arr;
    const padding = new Array(minSize - arr.length).fill(padWith);
    return [...padding, ...arr];
}
/**
 * Pad array at end to minimum size
 * @param arr - The array to pad
 * @param minSize - Minimum size of resulting array
 * @param padWith - Value to use for padding
 * @returns Padded array
 */
function padArrayEnd(arr, minSize, padWith) {
    if (arr.length >= minSize)
        return arr;
    const padding = new Array(minSize - arr.length).fill(1).map(padWith);
    return [...arr, ...padding];
}

export { arrayLast, arrayRandomElement, arrayRandomElements, chunk, distinct, getRandomElementWithSeed, groupBy, padArrayEnd, padArrayStart, shuffleArray, subtractArrays };
//# sourceMappingURL=array.esm.js.map
